import { useParams, useLocation, Link } from "react-router-dom";
import { useState, useRef, useCallback, useEffect } from "react";
import { useGetGenreMediaQuery } from "../../store/api/genreApi";
import PosterCard from "../../components/PosterCard/index.tsx";
import { MediaData } from "../../utils/types/app.types";

const GenreDetailPage = () => {
  const { genreId, mediaType } = useParams<{ genreId: string; mediaType: "movie" | "tv" }>();
  const location = useLocation();

  const [showPopularOnly, setShowPopularOnly] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [allItems, setAllItems] = useState<MediaData[]>([]);
  const [hasMore, setHasMore] = useState(true);
  const [genreName, setGenreName] = useState<string>("");
  const [showBackToTop, setShowBackToTop] = useState(false);

  const { data, isLoading, error, refetch } = useGetGenreMediaQuery({
    mediaType: mediaType!,
    genreId: genreId!,
    page: currentPage,
    sortBy: showPopularOnly ? 'popularity.desc' : 'release_date.desc'
  });

  // Fetch genre name from TMDB API
  useEffect(() => {
    const fetchGenreName = async () => {
      try {
        const response = await fetch(
          `https://api.themoviedb.org/3/genre/${mediaType}/list?api_key=db1a3d4fc9949395b6300a619305310d`
        );
        const data = await response.json();
        const genre = data.genres?.find((g: any) => g.id.toString() === genreId);
        if (genre) {
          setGenreName(genre.name);
        } else {
          // Fallback to location state or default
          setGenreName(location.state?.genreName || "Selected Genre");
        }
      } catch (error) {
        console.error("Failed to fetch genre name:", error);
        setGenreName(location.state?.genreName || "Selected Genre");
      }
    };

    if (mediaType && genreId) {
      fetchGenreName();
    }
  }, [mediaType, genreId, location.state?.genreName]);

  // Reset when filters change
  useEffect(() => {
    setCurrentPage(1);
    setAllItems([]);
    setHasMore(true);
  }, [showPopularOnly, genreId, mediaType]);

  // Add new items when data changes
  useEffect(() => {
    if (data?.items) {
      let sortedItems = [...data.items];
      
      // Custom sorting: by release date (newest first), then by rating (highest first)
      sortedItems.sort((a: MediaData, b: MediaData) => {
        // Get release dates
        const dateA = new Date(a.release_date || a.first_air_date || 0).getTime();
        const dateB = new Date(b.release_date || b.first_air_date || 0).getTime();
        
        // If dates are different, sort by date (newest first)
        if (dateA !== dateB) {
          return dateB - dateA;
        }
        
        // If dates are the same, sort by rating (highest first)
        const ratingA = a.vote_average || 0;
        const ratingB = b.vote_average || 0;
        return ratingB - ratingA;
      });
      
      if (currentPage === 1) {
        setAllItems(sortedItems);
      } else {
        // Prevent duplicates by checking if item already exists
        setAllItems(prev => {
          const existingIds = new Set(prev.map((item: MediaData) => item.id));
          const newItems = sortedItems.filter((item: MediaData) => !existingIds.has(item.id));
          return [...prev, ...newItems];
        });
      }
      setHasMore(currentPage < (data.totalPages || 0));
    }
  }, [data, currentPage]);

  // Intersection Observer for infinite scroll
  const observer = useRef<IntersectionObserver>();
  const lastItemRef = useCallback((node: HTMLDivElement) => {
    if (isLoading) return;
    
    if (observer.current) observer.current.disconnect();
    
    observer.current = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting && hasMore) {
        setCurrentPage(prev => prev + 1);
      }
    });
    
    if (node) observer.current.observe(node);
  }, [isLoading, hasMore]);

  // Scroll detection for back to top button
  useEffect(() => {
    const handleScroll = () => {
      setShowBackToTop(window.scrollY > 300);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Back to top function
  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  if (isLoading && currentPage === 1) {
    return (
      <div className="p-6 max-w-6xl mx-auto">
        <div className="text-center py-8">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-white"></div>
          <p className="mt-2 text-gray-400">Loading...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6 max-w-6xl mx-auto">
        <div className="text-center py-8">
          <p className="text-red-400">Error loading data. Please try again.</p>
          <button 
            onClick={() => refetch()} 
            className="mt-4 px-4 py-2 bg-[#092c4c] text-white rounded hover:bg-[#0c4574]"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  const genreDetailQuery = `from=genre&genreName=${encodeURIComponent(genreName)}&genreId=${genreId}&mediaType=${mediaType}`;

  return (
    <section className="container page-section">
      {/* Back to Genre List Button */}
      <div className="mb-6">
        <Link
          to={`/genres/${mediaType}`}
          className="inline-flex items-center bg-[#092c4c] text-white px-4 py-2 rounded-xl hover:bg-[#0c4574] shadow transition"
        >
          <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Back to {mediaType === "tv" ? "TV" : "Movie"} Genres
        </Link>
      </div>

      <h2 className="page-section-title">
        {mediaType === "tv" ? `TV Shows in ${genreName}` : `Movies in ${genreName}`}
      </h2>

      <div className="mb-4 flex justify-center space-x-4">
        <button
          type="button"
          onClick={() => setShowPopularOnly(true)}
          className={`px-4 py-2 rounded ${
            showPopularOnly
              ? "bg-[#092c4c] hover:bg-[#0c4574] text-white"
              : "bg-gray-200 text-gray-800 hover:bg-gray-300"
          }`}
        >
          Popular
        </button>
        <button
          type="button"
          onClick={() => setShowPopularOnly(false)}
          className={`px-4 py-2 rounded ${
            !showPopularOnly
              ? "bg-[#092c4c] hover:bg-[#0c4574] text-white"
              : "bg-gray-200 text-gray-800 hover:bg-gray-300"
          }`}
        >
          All
        </button>
      </div>

      {allItems.length === 0 ? (
        <div className="text-center py-8">
          <p className="text-gray-400">No items found in this genre.</p>
        </div>
      ) : (
        <div className="movie-grid">
          {allItems.map((item: MediaData, index: number) => (
            <div
              key={item.id}
              ref={index === allItems.length - 1 ? lastItemRef : undefined}
            >
              <PosterCard
                linkTo={`/${mediaType}-details/${item.id}?${genreDetailQuery}`}
                title={item.title || item.name || ""}
                posterPath={item.poster_path}
                subtitle={
                  mediaType === "tv"
                    ? `First air: ${item.first_air_date ?? "—"}`
                    : `Release: ${item.release_date ?? "—"}`
                }
              />
            </div>
          ))}
        </div>
      )}

      {isLoading && currentPage > 1 && (
        <div className="text-center py-4">
          <div className="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-white"></div>
          <p className="mt-2 text-gray-400">Loading more...</p>
        </div>
      )}

      {data && allItems.length > 0 && (
        <div className="mt-6 text-center text-gray-400">
          Showing {allItems.length} of {data.totalResults} results
          {hasMore && <span className="ml-2">• Scroll for more</span>}
        </div>
      )}

      {showBackToTop && (
        <button
          type="button"
          onClick={scrollToTop}
          className="fixed bottom-4 right-4 bg-[#092c4c] text-white px-4 py-2 rounded-xl hover:bg-[#0c4574] shadow transition"
        >
          Back to Top
        </button>
      )}
    </section>
  );
};

export default GenreDetailPage;